import {
  Box,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../context/userContext";
import {
  FeedbackMails,
  FEEDBACK_TYPE_BUGS,
  FEEDBACK_TYPE_BUG_REPORT,
  FEEDBACK_TYPE_FEATURE_REQ,
  FEEDBACK_TYPE_FEEDBACK,
  IAttachment,
} from "../../../model";
import { UserLoginInfo } from "../../../types/context";
import { appLogger, Http } from "../../../utils";
import { IconWithToolTip } from "../../common";
import { getDateTime } from "../../../utils/data";
import RenderInputBox from "./renderInputBox";
import { devConfig } from "../../../utils/config/devConfig";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { withSnackbar } from "../../../utils/common/SnackbarHoc";

interface IProps {
  handleDrawerOpen: any;
  open: boolean;
  setOpen: any;
  details: any;
  snackbarShowMessage: any;
}

const compareMailDates = (a: any, b: any) => {
  return a.createdOn > b.createdOn ? 1 : -1;
};

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: "0px",
  borderTop: "1px solid rgba(0, 0, 0, .125)",
  borderRadius: "0px",
}));

const Mailing = (props: IProps) => {
  appLogger.info({
    infoObj: {
      method: "Mailing",
      component: "mailing",
      fileName: "mailimgDrawer.tsx",
      prpos: props,
    },
  });
  const { open, handleDrawerOpen, setOpen, details } = props;
  const { userInfo } = useContext(UserContext) as UserLoginInfo;
  const [mails, setMails] = useState<FeedbackMails[]>([]);
  const [feedbackComments, setFeedbackComments] = useState<any>({});
  const [newMessage, setNewMessage] = useState("");
  const [newCC, setNewCC] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [productVal, setProductVal] = useState({});
  const [base64, setBase64] = useState<any>("");
  const [fileName, setFileName] = useState("");
  const [fileType, setFileType] = useState("");
  const [loader, setLoader] = useState(false);
  const [expanded, setExpanded] = React.useState<string | false>("");

  useEffect(() => {
    Http.post({
      url: `/api/v2/marknotification/${details.id}`,
      state: userInfo,
    })
      .then((response: any) => {})
      .catch((error: any) => {});
  }, []);

  useEffect(() => {
    if (details.feedbackComments) {
      const feedbackCommentsJson = JSON.parse(details.feedbackComments);
      setFeedbackComments(feedbackCommentsJson);
    }
    setLoader(true);
    setMails([]);
    fetchProductDetail();
    fetchMails();
  }, [details]);

  const handleAttachmentClick = async (
    messageId: string,
    attachmentId: string,
    filename: string,
    contentType: string
  ) => {
    try {
      setLoader(true);
      const messageIdTrimmed = messageId.substring(1, messageId.length - 1);
      const attachmentData: any = await Http.get({
        url: `/api/v2/gmailattachment?messageId=${messageIdTrimmed}&attachmentId=${attachmentId}`,
        state: userInfo,
      });
      const parsedData = JSON.parse(attachmentData.data);
      const dataModified = parsedData.data
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      const base64 = `data:${contentType};base64,${dataModified}`;
      const a = document.createElement("a");
      a.href = base64;
      a.download = filename;
      a.click();
      setLoader(false);
    } catch (error) {
      setLoader(false);
      props.snackbarShowMessage("Error in downloading attachment", "error");
      appLogger.error({
        infoObj: {
          method: "fetchAttachments",
          component: "mailing",
          fileName: "mailimgDrawer.tsx",
          error: error,
        },
      });
    }
  };

  const fetchMails = async () => {
    try {
      const mailData: any = await Http.get({
        url: `/api/v2/mailing/${details.id}`,
        state: userInfo,
      });
      setLoader(false);
      appLogger.info({
        infoObj: {
          method: "fetchMails",
          component: "mailing",
          fileName: "mailimgDrawer.tsx",
          mailData: mailData,
        },
      });
      if (mailData && mailData.length) {
        mailData.sort(compareMailDates);
        setMails(mailData);
        const ccValue = mailData[mailData.length - 1].cc;
        // const subValue = mailData[mailData.length - 1].subject;
        let subValue = "";
        mailData.map((mail: any) => {
          if (mail.subject) {
            subValue = mail.subject;
          }
        });
        setNewCC(ccValue ? ccValue : "");
        setNewSubject(subValue ? subValue : "");
        setExpanded(`panel${mailData.length}`);
      } else {
        setNewCC("");
        setNewSubject("");
      }
    } catch (error) {
      setLoader(false);
      props.snackbarShowMessage("Error in fetching new mails", "error");
      appLogger.error({
        infoObj: {
          method: "fetchMails",
          component: "mailing",
          fileName: "mailimgDrawer.tsx",
          error: error,
        },
      });
    }
  };

  const fetchProductDetail = async () => {
    setLoader(true);
    setNewMessage("");
    Http.get({
      url: `/api/v2/products/${details.productId}/${details.productVersion}`,
      state: userInfo,
    })
      .then((response: any) => {
        setLoader(false);
        appLogger.info({
          infoObj: {
            method: "useEffect",
            component: "add-products",
            fileName: "index.tsx",
            productResponse: response,
          },
        });
        if (response && response.products && response.products.length > 0) {
          let defaultMsgTemplate = "";
          setProductVal(response.products[0]);
          if (response.products[0] && response.products[0].emailConfig) {
            const fdbkType =
              details.feedbackType === FEEDBACK_TYPE_BUG_REPORT
                ? FEEDBACK_TYPE_BUGS
                : details.feedbackType;
            if (
              response.products[0].emailConfig.emailSalutation &&
              response.products[0].emailConfig.emailSalutation[fdbkType]
            ) {
              defaultMsgTemplate +=
                response.products[0].emailConfig.emailSalutation[fdbkType];
            }
            if (
              response.products[0].emailConfig.emailOpeningParagraph &&
              response.products[0].emailConfig.emailOpeningParagraph[fdbkType]
            ) {
              defaultMsgTemplate +=
                response.products[0].emailConfig.emailOpeningParagraph[
                  fdbkType
                ];
              defaultMsgTemplate += "<br><br></br></br>";
            } else {
              defaultMsgTemplate += "<br><br></br></br>";
            }
            if (
              response.products[0].emailConfig.emailClosingParagraph &&
              response.products[0].emailConfig.emailClosingParagraph[fdbkType]
            ) {
              defaultMsgTemplate +=
                response.products[0].emailConfig.emailClosingParagraph[
                  fdbkType
                ];
            }
            if (
              response.products[0].emailConfig.emailSignature &&
              response.products[0].emailConfig.emailSignature[fdbkType]
            ) {
              defaultMsgTemplate +=
                response.products[0].emailConfig.emailSignature[fdbkType];
            }
          }
          handleMessageChange(defaultMsgTemplate);
        }
        // setFeedbackAgentSettingsFetched(true);
      })
      .catch((error: any) => {
        appLogger.error({
          errObj: {
            method: "useEffect",
            component: "add-products",
            fileName: "index.tsx",
            err: error,
          },
        });
        // setFailure(true);
      });
  };

  const handleMessageChange = (event: any) => {
    setNewMessage(event);
  };

  const handleCC = (event: any) => {
    setNewCC(event.target.value);
  };

  const handleSubject = (event: any) => {
    setNewSubject(event.target.value);
  };

  const sendMail = async () => {
    const feedbackCommentsJson = JSON.parse(details.feedbackComments);

    const body = {
      body: newMessage,
      subject: newSubject
        ? newSubject
        : `Regarding ${feedbackCommentsJson.generalComment}`,
      feedbackId: details.id,
      base64: base64,
      filename: fileName,
      cc: newCC,
    };
    setLoader(true);
    await Http.post({
      url: `/api/v2/mailing/`,
      body,
      state: userInfo,
    });
    appLogger.info(
      {
        infoObj: {
          method: "sendMail",
          component: "mailing",
          fileName: "mailimgDrawer.tsx",
        },
      },
      "Mail Send"
    );
    setNewMessage("");
    setNewCC("");
    setNewSubject("");
    setFileName("");
    setFileType("");
    setBase64("");
    fetchMails();
    fetchProductDetail();
  };

  const stripTags = (unStripped: any) => {
    const parseHTML = new DOMParser().parseFromString(unStripped, "text/html");
    return (
      <>
        <div dangerouslySetInnerHTML={{ __html: unStripped || "" }}></div>
      </>
    );
  };

  const renderLink = (attachment: IAttachment, messageId: string) => {
    return (
      <div>
        <Link
          id={attachment.attachmentId}
          onClick={() => {
            handleAttachmentClick(
              messageId,
              attachment.attachmentId,
              attachment.filename,
              attachment.contentType
            );
          }}
          title="Attachment"
        >
          {attachment.filename}
        </Link>
      </div>
    );
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const RenderMailList = () => {
    return (
      <div className="feedbackMailAccount">
        <>
          {mails &&
            mails.length > 0 &&
            mails.map((mail: FeedbackMails, index: number) => {
              let bodySplit: string[] = [];
              if (mail && mail.body) {
                bodySplit = mail.body.split("\r\n");
              }
              return (
                <Accordion
                  expanded={expanded === "panel" + `${index + 1}`}
                  onChange={handleChange("panel" + `${index + 1}`)}
                >
                  <AccordionSummary
                    aria-controls="panel1d-content"
                    id="panel1d-header"
                    className="feedbackMailAccount-accordionsummary"
                  >
                    <Grid container>
                      <Grid item xs={11}>
                        <Typography
                          style={{ paddingTop: "9px", fontSize: "12px" }}
                        >
                          {mail.createdBy === devConfig.feedbackMailAccount
                            ? `Mail to user on `
                            : `Reply from user on `}
                          {getDateTime(mail.createdOn)}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        className="logo-txt"
                        style={{ maxWidth: "40px" }}
                        xs={1}
                      >
                        <Typography>
                          {mail.createdBy[0].toUpperCase()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails className="feedbackMailAccount-accordiondetail">
                    <Box className="render-chat-list">
                      {mail.createdBy === devConfig.feedbackMailAccount ? (
                        <Grid
                          container
                          style={{ justifyContent: "space-between" }}
                          id={mail.messageId}
                        >
                          <Grid item className="message-box">
                            <div
                              style={{
                                color: "#000",
                                backgroundColor: "#EFEFEF",
                              }}
                            >
                              <div style={{ padding: "8px 8px 0px 8px" }}>
                                {bodySplit &&
                                  bodySplit.length > 0 &&
                                  bodySplit.map((bodyPart, i) => {
                                    return (
                                      <div id={i.toString()}>
                                        {stripTags(bodyPart)}
                                      </div>
                                    );
                                  })}
                                <div>
                                  {mail.attachmentInfo &&
                                  mail.attachmentInfo.length > 0 ? (
                                    <div>
                                      {mail.attachmentInfo.map((attachment) =>
                                        renderLink(attachment, mail.messageId)
                                      )}
                                    </div>
                                  ) : (
                                    <div />
                                  )}
                                </div>
                              </div>
                              {/* <div
                                  style={{
                                    fontSize: "12px",
                                    textAlign: "right",
                                    padding: "0px 8px 5px 0px",
                                  }}
                                >
                                  {getDateTime(mail.createdOn)}
                                </div> */}
                            </div>
                          </Grid>
                          {/* <Grid item className="logo-txt">
                              <Typography>{mail.createdBy[0].toUpperCase()}</Typography>
                            </Grid> */}
                        </Grid>
                      ) : (
                        <Grid container id={mail.messageId}>
                          {/* <Grid item className="logo-txt">
                              <Typography>{mail.createdBy[0].toUpperCase()}</Typography>
                            </Grid> */}
                          <Grid item className="message-box">
                            <div style={{ backgroundColor: "#E9E9E9" }}>
                              <div style={{ padding: "8px 8px 0px 8px" }}>
                                {bodySplit.map((bodyPart, i) => {
                                  if (!bodyPart) {
                                    return (
                                      <div id={i.toString()}>
                                        <br />
                                      </div>
                                    );
                                  }
                                  return (
                                    <div id={i.toString()}>{bodyPart}</div>
                                  );
                                })}
                                <div>
                                  {mail.attachmentInfo &&
                                  mail.attachmentInfo.length > 0 ? (
                                    <div>
                                      {mail.attachmentInfo.map((attachment) =>
                                        renderLink(attachment, mail.messageId)
                                      )}
                                    </div>
                                  ) : (
                                    <div />
                                  )}
                                </div>
                              </div>
                              {/* <div
                                  style={{
                                    fontSize: "12px",
                                    textAlign: "right",
                                    padding: "0px 8px 5px 0px",
                                  }}
                                >
                                  {getDateTime(mail.createdOn)}
                                </div> */}
                            </div>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </>
      </div>
    );
  };

  return (
    <div>
      <Drawer
        className="mail-popup"
        anchor="right"
        open={open}
        onClose={handleDrawerOpen}
      >
        <Box className={loader ? "mail-popup-wrap loader" : "mail-popup-wrap"}>
          <Box
            className="title-wrap"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Grid container sx={{ alignItems: "center" }}>
              <Grid item>
                <Typography
                  className="title"
                  style={{ textTransform: "lowercase" }}
                >
                  {"Mail to: "}
                  <span style={{ color: "#737373" }}>
                    {details.userId ? details.userId : "Anonymous"}
                  </span>
                </Typography>
              </Grid>

              <Grid item sx={{ flexGrow: 1, paddingTop: "8px" }}>
                {details.feedbackType == FEEDBACK_TYPE_FEEDBACK ? (
                  <Rating
                    className={`ratings-clr-${details.productRating}`}
                    name="read-only"
                    value={details.productRating || 0}
                    readOnly
                  />
                ) : details.feedbackType == FEEDBACK_TYPE_FEATURE_REQ ? (
                  <></>
                ) : (
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      flexWrap: "wrap",
                    }}
                    className="ratings"
                  >
                    {details.bugPriority ? (
                      <Chip
                        label={details.bugPriority}
                        color="primary"
                        variant="outlined"
                        className="bug-chip-text"
                      />
                    ) : (
                      <h6 className="img-prv-lable">Unknown</h6>
                    )}
                  </Stack>
                )}
              </Grid>

              <Grid item>
                <IconButton
                  className="close-report"
                  style={{ fontSize: "1rem" }}
                  onClick={() => setOpen(!open)}
                >
                  <IconWithToolTip message="Close" iconName="icon-close" />
                </IconButton>
              </Grid>
            </Grid>
          </Box>

          <Box className="mail-content-wrap">
            <Box>
              <Grid container>
                <Grid item sm={6}>
                  <Typography className="title-sm">
                    Category :{" "}
                    <span style={{ color: "#737373" }}>
                      {details.feedbackCategory}
                    </span>
                  </Typography>
                  {feedbackComments.standardFeedback ? (
                    <div>
                      {feedbackComments.standardFeedback.map((el: string) => {
                        return (
                          <Typography id={el} className="info">
                            {el}
                          </Typography>
                        );
                      })}
                    </div>
                  ) : (
                    <div />
                  )}
                </Grid>
                <Grid item sm={6}>
                  <Typography className="title-sm">General Comment</Typography>
                  <Typography className="info">
                    {feedbackComments.generalComment}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Divider sx={{ my: 2 }} />

            <RenderMailList />
          </Box>

          <Box className="chat-input">
            <RenderInputBox
              label={mails.length > 0 ? "Reply" : "Send mail"}
              // label=""
              newMessage={newMessage}
              newCC={newCC}
              handleMessageChange={handleMessageChange}
              handleCC={handleCC}
              newSubject={newSubject}
              handleSubject={handleSubject}
              sendMail={sendMail}
              base64={base64}
              setBase64={setBase64}
              fileName={fileName}
              setFileName={setFileName}
              fileType={fileType}
              setFileType={setFileType}
            />
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default withSnackbar(Mailing);
